import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { fmtMin, fmtNum, fmtPct } from './format';
import { Kpi, alert, card, cardHead, muted, td, tdR, th, thR } from './Overview';

// "Produkcja" (spec 7.4): one table, one aggregation engine, a dimension switch. Clicking a
// row expands the cross-dimension breakdown (material -> lines, line -> materials, ...).

type Dim = 'material' | 'line' | 'shift' | 'brand' | 'sku' | 'color';
const DIMS: Dim[] = ['material', 'line', 'shift', 'brand', 'sku', 'color'];
const DEFAULT_SUB: Record<Dim, Dim> = { material: 'line', line: 'material', shift: 'line', brand: 'material', sku: 'line', color: 'material' };

interface Row extends Kpi { key: string; label: string; shareKg: number | null; sharePcs: number | null }
interface Data { dimension: Dim; rows: Row[]; drill: { key: string; dimension: Dim; rows: Row[] } | null }

const Production: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [dim, setDim] = useState<Dim>('material');
  const [drill, setDrill] = useState<{ key: string; sub: Dim } | null>(null);
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDrill(null); }, [dim, filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: Record<string, string> = { ...toQuery(filters) };
    if (drill) { params.drill = drill.key; params.sub = drill.sub; }
    api.get(`/analytics/dimensions/${dim}`, { params })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, dim, drill, t]);

  const total = (rows: Row[]) => rows.reduce((a, r) => ({ pcs: a.pcs + r.pcs, kgNet: a.kgNet + r.kgNet, kgFlow: a.kgFlow + r.kgFlow, prodMin: a.prodMin + r.prodMin, downMin: a.downMin + r.downMin }), { pcs: 0, kgNet: 0, kgFlow: 0, prodMin: 0, downMin: 0 });
  const tot = data ? total(data.rows) : null;
  const best = data?.drill?.rows.filter(r => r.kgPerH !== null).sort((a, b) => (b.kgPerH ?? 0) - (a.kgPerH ?? 0)) ?? [];

  const RowCells: React.FC<{ r: Row; sub?: boolean }> = ({ r, sub }) => (
    <>
      <td style={{ ...td, fontWeight: sub ? 500 : 700, paddingLeft: sub ? 32 : 12, whiteSpace: 'normal' }}>{r.label}</td>
      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(r.pcs)}</td>
      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(r.kgNet, 1)}</td>
      <td style={tdR}>{r.shareKg === null ? '—' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 60, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', display: 'inline-block' }}><span style={{ display: 'block', width: `${r.shareKg * 100}%`, height: '100%', background: '#667eea' }} /></span>{fmtPct(r.shareKg)}</span>}</td>
      <td style={tdR}>{fmtNum(r.kgFlow, 1)}</td>
      <td style={tdR}>{r.hasTelemetry ? fmtMin(r.prodMin) : '—'}</td>
      <td style={tdR}>{r.hasTelemetry ? fmtMin(r.downMin) : '—'}</td>
      <td style={tdR}>{fmtPct(r.availability)}</td>
      <td style={tdR}>{fmtNum(r.avgSpeed, 1)}</td>
      <td style={{ ...tdR, color: r.performance === null ? undefined : r.performance >= 0.9 ? '#059669' : r.performance >= 0.75 ? '#d97706' : '#dc2626', fontWeight: 700 }}>{fmtPct(r.performance)}</td>
      <td style={tdR}>{fmtNum(r.kgPerH, 1)}</td>
    </>
  );

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🧱 {t('production.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('production.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={setFilters} showCompare={false} />
      {error && <div style={alert}>{error}</div>}

      <div style={card}>
        <div style={{ ...cardHead, alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2, flexWrap: 'wrap' }}>
            {DIMS.map(d => (
              <button key={d} type="button" onClick={() => setDim(d)} style={{ border: 'none', background: dim === d ? '#fff' : 'transparent', padding: '6px 12px', borderRadius: 6, fontSize: 13, color: dim === d ? '#1e293b' : '#475569', fontWeight: dim === d ? 700 : 500, cursor: 'pointer', boxShadow: dim === d ? '0 1px 2px rgba(0,0,0,0.08)' : undefined }}>
                {t(`production.dim_${d}`)}
              </button>
            ))}
          </div>
          <span style={muted}>{t('production.clickHint', { sub: t(`production.dim_${DEFAULT_SUB[dim]}`) })}</span>
        </div>

        <div style={{ overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={th}>{t(`production.dim_${dim}`)}</th><th style={thR}>{t('analytics.colPcs')}</th><th style={thR}>{t('analytics.colKg')}</th><th style={thR}>{t('production.colShare')}</th><th style={thR}>{t('analytics.colKgFlow')}</th>
                <th style={thR}>{t('analytics.colProd')}</th><th style={thR}>{t('analytics.colDown')}</th><th style={thR}>Availability</th><th style={thR}>{t('analytics.colSpeed')}</th><th style={thR}>Performance</th><th style={thR}>kg/h</th>
              </tr>
            </thead>
            <tbody>
              {!data ? (
                <tr><td colSpan={11} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('common.loading')}</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={11} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('production.empty')}</td></tr>
              ) : data.rows.map(r => {
                const open = drill?.key === r.key;
                return (
                  <React.Fragment key={r.key}>
                    <tr onClick={() => setDrill(open ? null : { key: r.key, sub: DEFAULT_SUB[dim] })} style={{ cursor: 'pointer', background: open ? '#eef2ff' : undefined }}>
                      <RowCells r={{ ...r, label: `${open ? '▾' : '▸'} ${r.label}` }} />
                    </tr>
                    {open && data.drill && data.drill.key === r.key && (
                      <>
                        <tr style={{ background: '#f8fafc' }}>
                          <td colSpan={11} style={{ ...td, fontSize: 12, color: '#475569' }}>
                            <span style={{ fontWeight: 700 }}>{r.label}</span> → {t('production.byDim')}:
                            {DIMS.filter(d => d !== dim).map(d => (
                              <button key={d} type="button" onClick={e => { e.stopPropagation(); setDrill({ key: r.key, sub: d }); }} style={{ marginLeft: 6, padding: '3px 8px', borderRadius: 5, border: '1px solid #cbd5e1', background: drill?.sub === d ? '#667eea' : '#fff', color: drill?.sub === d ? '#fff' : '#475569', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{t(`production.dim_${d}`)}</button>
                            ))}
                            {best.length > 1 && (
                              <span style={{ marginLeft: 16, color: '#64748b' }}>
                                {t('production.bestWorst', { best: best[0].label, bestV: fmtNum(best[0].kgPerH, 1), worst: best[best.length - 1].label, worstV: fmtNum(best[best.length - 1].kgPerH, 1) })}
                              </span>
                            )}
                          </td>
                        </tr>
                        {data.drill.rows.map(s => <tr key={`${r.key}-${s.key}`} style={{ background: '#fbfcfe' }}><RowCells r={s} sub /></tr>)}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {tot && (
              <tfoot>
                <tr style={{ background: '#f8f9fa', fontWeight: 700 }}>
                  <td style={td}>{t('analytics.total')}</td><td style={tdR}>{fmtNum(tot.pcs)}</td><td style={tdR}>{fmtNum(tot.kgNet, 1)}</td><td style={tdR}>100%</td><td style={tdR}>{fmtNum(tot.kgFlow, 1)}</td>
                  <td style={tdR}>{fmtMin(tot.prodMin)}</td><td style={tdR}>{fmtMin(tot.downMin)}</td><td style={tdR}></td><td style={tdR}></td><td style={tdR}></td><td style={tdR}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default Production;
